'use client'

import { useTranslations } from 'next-intl'
import { CheckCircleIcon } from 'primitives/icons'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes, AppRoutesParameterized } from '@repo/web-utils/app-routes'
import { tw } from 'utils/classnames'
import { usePathname } from 'next/navigation'
import * as React from 'react'
import { Tooltip } from 'primitives/tooltip'
import { SkeletonItem } from 'components/skeleton-item'
import Image from 'next/image'

import { PeopleIcon, LoadingIcon } from '../../../assets/icons'
import { JoinClub } from './join-club'
import { RecommendedClubhouses } from './recommended-clubhouses'
import { useClubhousesGetDetailsQuery } from '@web/gen/clubhouses'
import { useClubhouseOnlineCount } from '../../../_hooks/use-clubhouse-online-count'
import { formatString } from '../../../_utils/format-string'
import { formatNumberToNumericalAbreviation } from 'utils/number'
import { PromotionSidebar } from './promotion'
import { AllAccessBenefits } from './all-access-benefits'
import { JoinButton } from '../join-button'
import { useMenuModels } from '../../_hooks/use-menu-models'
import type { Clubhouse } from '@web/gen/types'

const sidebarItemClass = `hover:bg-cf-dreamy-cloud-300 flex w-full flex-row items-center justify-start rounded p-2 cursor-pointer text-[14px] font-medium  leading-[16px]`

export function Sidebar({
  slug,
  initialClubhouse,
}: {
  slug: string
  initialClubhouse?: Clubhouse | null
}) {
  const pathname = usePathname()
  const t = useTranslations('pages.clubhouse')
  const { data: clubhouse, isPending } = useClubhousesGetDetailsQuery(
    {
      input: {
        clubhouseSlug: slug,
      },
    },
    { select: data => data?.clubhousesGetDetails?.clubhouse },
  )

  const onlineCount = useClubhouseOnlineCount(slug)

  const { sidebarModel } = useMenuModels({ clubhouseSlug: slug })
  const clubhouseName = clubhouse?.name || initialClubhouse?.name
  const clubhouseTotalMembers = clubhouse?.membersTotal || initialClubhouse?.membersTotal

  return (
    <aside
      id="sidebar"
      className="relative hidden w-64 shrink-0 flex-col self-stretch overflow-visible lg:flex"
    >
      <section className="bg-cf-white rounded-t-[12px] w-full  overflow-hidden border-x border-t border-[#DADCE2]">
        <div className="bg-cf-cricut-green relative h-11 w-full">
          {isPending ? (
            <SkeletonItem className="absolute left-[calc(50%-28px)] top-4 h-[56px] w-[56px] rounded-full" />
          ) : (
            <div
              className={tw(
                'border-cf-white absolute left-[calc(50%-28px)] top-4 flex h-[56px] w-[56px] items-center justify-center rounded-full border-[3px]',
                `bg-[${clubhouse?.bgColor}]`,
              )}
            >
              <Image src={clubhouse?.avatarImage || ''} alt="logo" width={66} height={66} />
            </div>
          )}
        </div>

        <div className="mt-10 flex w-full flex-col items-center justify-start ">
          <div className="flex flex-row items-center justify-center">
            <Link className="text-16  mr-1 font-semibold" href={`${AppRoutes.CLUBHOUSE}${slug}`}>
              {clubhouseName} {t('sidebar.title')}
            </Link>
            <Tooltip
              value={
                <div className="text-cf-white bg-cf-black text-12 rounded-2 max-w-[200px] p-3  text-center shadow-md ">
                  {t('sidebar.tooltip')}
                </div>
              }
              arrowClassName="fill-cf-black"
              delay={0}
              side="bottom"
              hideArrow={false}
              className="p-0"
              avoidCollisions
            >
              <div className="size-3">
                <CheckCircleIcon className="text-cf-cricut-green size-3 " />
              </div>
            </Tooltip>
          </div>
          {isPending ? (
            <SkeletonItem className="h-[21px] w-[100px]" />
          ) : (
            <Link
              href={`${AppRoutesParameterized.CLUBHOUSE_SEARCH_RESULTS(clubhouse?.type)}`}
              className="text-14 text-gray-200"
            >
              {formatString(clubhouse?.type)}
            </Link>
          )}
          <div className="mt-4 flex flex-row items-center justify-center">
            <PeopleIcon className="text-cf-cricut-green mb-1 mr-1  size-5" />
            <p className="text-14  font-medium">
              {formatNumberToNumericalAbreviation(Number(clubhouseTotalMembers))} {t('members')}
            </p>
          </div>
          <div className=" mt-1 flex flex-row items-center justify-center">
            <div className="bg-cf-cricut-green relative  mr-1.5 flex size-2  self-center rounded-full">
              <div className="bg-cf-cricut-green absolute flex size-2 animate-ping self-center rounded-full" />
            </div>

            <div className="flex flex-row ">
              {onlineCount ? (
                <>
                  <span className="text-14 mr-1  font-medium ">{onlineCount}</span>
                  <p className="text-14 font-medium ">{t('online')}</p>
                </>
              ) : (
                <div className="flex items-center justify-center">
                  <LoadingIcon className="mr-1 size-3 animate-spin" />
                  <p className="text-14 font-medium text-transparent">{t('online')}</p>
                </div>
              )}
            </div>
          </div>
          <div className="mt-0.5 w-full p-4">
            <JoinButton slug={slug} className="w-full" clubhouseName={clubhouse?.name} />
          </div>
        </div>
      </section>
      <section className="bg-cf-white rounded-b-[12px] w-full overflow-hidden border border-[#DADCE2]">
        <div className="flex w-full flex-col items-start justify-start px-4 pb-7 ">
          {sidebarModel
            .filter(item => item.isActive)
            .map((item, index) => {
              if (item.type === 'paragraph') {
                return (
                  <p className="text-12 mt-4 py-1 pl-2.5 font-normal" key={'p_' + index}>
                    {item.text}
                  </p>
                )
              }
              // oxlint-disable-next-line array-callback-return
              if (item.type === 'link') {
                const Icon = item.icon
                return (
                  <Link
                    key={'a_' + index}
                    className={tw(
                      pathname === item.path + '/' ? 'bg-[#EEEFF2]' : '',
                      sidebarItemClass,
                    )}
                    href={item.path}
                  >
                    {Icon ? <Icon className="mr-1 size-4" /> : null}
                    {item.text}
                  </Link>
                )
              }
            })}
        </div>
      </section>
      <section className="mt-4">
        <JoinClub />
      </section>

      <div className="sticky top-0">
        <div className="my-4">
          <RecommendedClubhouses clubhouseSlug={slug} clubhouseName={clubhouse?.name || ''} />
        </div>
        <PromotionSidebar
          clubhouseSlug={slug}
          clubhouseType={formatString(clubhouse?.type)}
          clubhouseName={clubhouse?.name}
        />
        <AllAccessBenefits clubhouseSlug={slug} />
      </div>
    </aside>
  )
}
